import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Share2, CalendarPlus } from "lucide-react";
import { FadeIn } from "@/components/common/FadeIn";
import { SEO } from "@/components/common/SEO";
import { RSVPModal } from "@/components/common/RSVPModal";
import { NotFound } from "@/components/common/NotFound";
import api from "@/lib/api";

function formatEventDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function splitParagraphs(text) {
  if (!text) return [];
  return text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

/**
 * Parse a free-form time string like "6:00 PM", "6 PM", "18:00", or
 * "6:30pm" into { hours, minutes } (24-hour). Returns null if it can't
 * be parsed so the caller can fall back to an all-day event.
 */
function parseTimeString(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, "");
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = m[3];
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  if (ampm === "am") {
    if (h === 12) h = 0;
  } else if (ampm === "pm") {
    if (h < 12) h += 12;
  }
  return { hours: h, minutes: min };
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * Build a YYYYMMDDTHHMMSS local floating timestamp for the .ics file.
 * We deliberately emit floating local time (no Z, no TZID) so the
 * event lands at the gallery's wall-clock time on the attendee's
 * calendar — appropriate for an in-person Tulsa event.
 */
function toIcsLocalDt(dateStr, hours, minutes) {
  const [y, m, d] = dateStr.split("-").map((p) => parseInt(p, 10));
  return `${y}${pad2(m)}${pad2(d)}T${pad2(hours)}${pad2(minutes)}00`;
}

/**
 * Add 1 day to a YYYY-MM-DD string (used for all-day .ics DTEND, which
 * is exclusive per RFC 5545).
 */
function nextDay(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
}

function escapeIcsText(text) {
  return String(text || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function buildIcsContent(event) {
  const uid = `${event.id || event.slug || "event"}@rainboyfineart.com`;
  const dtstamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");

  const start = parseTimeString(event.time);
  const end = parseTimeString(event.end_time);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Rain Boy Fine Art//Events//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
  ];

  if (start) {
    const startDt = toIcsLocalDt(event.date, start.hours, start.minutes);
    let endDt;
    if (end) {
      endDt = toIcsLocalDt(event.date, end.hours, end.minutes);
    } else {
      // No end time provided — default to 2 hours after start.
      const totalMin = start.hours * 60 + start.minutes + 120;
      endDt = toIcsLocalDt(event.date, Math.floor(totalMin / 60) % 24, totalMin % 60);
    }
    lines.push(`DTSTART:${startDt}`);
    lines.push(`DTEND:${endDt}`);
  } else {
    // All-day event fallback.
    const startDate = event.date.replace(/-/g, "");
    lines.push(`DTSTART;VALUE=DATE:${startDate}`);
    lines.push(`DTEND;VALUE=DATE:${nextDay(event.date)}`);
  }

  lines.push(`SUMMARY:${escapeIcsText(event.title)}`);
  if (event.description) {
    lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  }
  const venue = (event.venue_name || "").trim();
  const address = (event.location || "").trim();
  const locationStr = [venue, address].filter(Boolean).join(", ");
  if (locationStr) {
    lines.push(`LOCATION:${escapeIcsText(locationStr)}`);
  }
  lines.push(`URL:${window.location.href}`);
  lines.push("END:VEVENT");
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

function downloadIcsFile(filename, content) {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Reshape the normalized event payload into the detail-page view model.
 * All copy now comes from admin-managed fields: pull_quote, content_sections,
 * venue_name, amenities, admission. No title-based special cases.
 */
function buildEventView(event) {
  const lede = (event.pull_quote || "").trim();

  const sections = (event.content_sections || [])
    .filter((s) => (s?.heading || "").trim() && (s?.body || "").trim())
    .map((s) => ({ label: s.heading.trim(), paragraphs: splitParagraphs(s.body) }))
    .filter((s) => s.paragraphs.length > 0);

  const details = [
    {
      key: "When",
      value: formatEventDate(event.date),
      secondary: `${event.time || ""}${event.end_time ? " — " + event.end_time : ""}`.trim() || null,
    },
  ];
  const venue = (event.venue_name || "").trim();
  const address = (event.location || "").trim();
  if (venue || address) {
    details.push({
      key: "Where",
      value: venue || address.split(",")[0],
      secondary: venue && address ? address : (venue ? address : null),
    });
  }
  if ((event.amenities || "").trim()) {
    details.push({ key: "Enjoy", value: event.amenities.trim() });
  }
  if ((event.admission || "").trim()) {
    details.push({ key: "Admission", value: event.admission.trim() });
  }

  return { lede, sections, details };
}

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [showRSVP, setShowRSVP] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    api.get(`/events/${id}`)
      .then((r) => {
        if (cancelled) return;
        setEvent(r.data);
        setLoadState("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadState(err?.response?.status === 404 ? "not_found" : "error");
      });
    return () => { cancelled = true; };
  }, [id]);

  if (loadState === "not_found" || loadState === "error") {
    return (
      <NotFound
        subject="event"
        backTo="/events"
        backLabel="All Events"
        isError={loadState === "error"}
      />
    );
  }

  if (loadState === "loading" || !event) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { lede, sections, details } = buildEventView(event);
  const eventType = event.event_type || "Gallery Event";

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: event.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleAddToCalendar = () => {
    const ics = buildIcsContent(event);
    const safeTitle = (event.title || "event")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "event";
    downloadIcsFile(`${safeTitle}.ics`, ics);
  };

  return (
    <div data-testid="event-detail-page" className="pt-20">
      <SEO
        title={`${event.title} | Rain Boy Fine Art Tulsa`}
        description={`${event.title} at Rain Boy Fine Art in Tulsa, OK. ${formatEventDate(event.date)}, ${event.time}${event.end_time ? " - " + event.end_time : ""}. ${event.description?.slice(0, 100) || ""}`}
        path={`/events/${id}`}
        image={event.image}
        type="article"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Events", path: "/events" }, { name: event.title, path: `/events/${id}` }]}
        schemas={[{
          "@context": "https://schema.org", "@type": "Event", name: event.title,
          description: event.description,
          startDate: `${event.date}T${event.time?.includes("PM") ? (parseInt(event.time) + 12) + ":00:00" : event.time}-05:00`,
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          location: { "@type": "Place", name: "Rain Boy Fine Art", address: { "@type": "PostalAddress", streetAddress: "3306 Charles Page Blvd #218", addressLocality: "Tulsa", addressRegion: "OK", postalCode: "74127", addressCountry: "US" } },
          image: event.image ? [event.image] : [],
          organizer: { "@type": "ArtGallery", name: "Rain Boy Fine Art", url: "https://rainboyfineart.com" },
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock", url: `https://rainboyfineart.com/events/${id}` },
        }]}
      />
      <section className="pt-8 md:pt-12 pb-16 md:pb-24 px-6 md:px-8 lg:px-12">
        <div className="max-w-[1280px] mx-auto">
          {/* Back link */}
          <Link
            to="/events"
            className="group inline-flex items-center gap-2 mb-12"
            style={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.68rem', letterSpacing: '0.26em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.55)', textDecoration: 'none' }}
            data-testid="back-to-events"
          >
            <ArrowLeft size={11} className="transition-transform duration-300 group-hover:-translate-x-[3px] group-hover:text-[#CBA365]" />
            <span className="transition-colors duration-300 group-hover:text-[#CBA365]">All Events</span>
          </Link>

          {/* Header */}
          <FadeIn>
            <div className="mb-12">
              {event.is_past && (
                <span className="inline-block bg-[#d3cac0]/40 text-[#4A5568] text-[10px] uppercase tracking-[0.2em] px-3 py-1 mb-4">
                  Past Event
                </span>
              )}
              <p className="overline mb-5">{eventType}</p>
              <h1
                className="section-heading font-heading text-[#0A1128]"
                style={{ fontSize: 'clamp(2.25rem, 4.2vw, 3.75rem)', lineHeight: 1.1 }}
              >
                {event.title}
              </h1>
            </div>
          </FadeIn>

          {/* Hero image */}
          {event.image && (
            <FadeIn delay={0.1}>
              <div className="aspect-[16/9] md:aspect-[16/9] overflow-hidden mb-12 md:mb-16">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 60%' }}
                />
              </div>
            </FadeIn>
          )}

          {/* Two-column body */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 lg:gap-20">
            {/* Left — narrative */}
            <FadeIn delay={0.15}>
              <div>
                {/* Lede */}
                {lede && (
                  <div
                    className="mb-12 pl-5 border-l border-[#CBA365] max-w-[640px]"
                    style={{ fontFamily: "'Outfit', sans-serif", fontStyle: 'italic', fontSize: '1.3rem', lineHeight: 1.5, color: '#0A1128', textAlign: 'left' }}
                  >
                    {lede}
                  </div>
                )}

                {/* Sections */}
                {sections.map((section, si) => (
                  <div key={si} className="mb-11">
                    <div className="flex items-center gap-2 mb-5">
                      <div className="w-5 h-[1px] bg-[#CBA365]" />
                      <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#CBA365' }}>
                        {section.label}
                      </span>
                    </div>
                    <div className="max-w-[640px]">
                      {section.paragraphs.map((p, pi) => (
                        <p
                          key={pi}
                          className="mb-5"
                          style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontSize: '1rem', lineHeight: 1.75, color: 'rgba(10, 25, 41, 0.8)', textAlign: 'left' }}
                        >
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>

            {/* Right — details sidebar */}
            <FadeIn delay={0.2}>
              <div className="bg-[#F8F9FA] p-8 lg:p-10 self-start">
                <div className="flex items-center gap-2 mb-7">
                  <div className="w-5 h-[1px] bg-[#CBA365]" />
                  <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.72rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#CBA365' }}>Event Details</span>
                </div>

                {/* Detail rows */}
                <div>
                  {details.map((detail, di) => (
                    <div
                      key={di}
                      className={di === 0 ? "border-t py-4" : di === details.length - 1 ? "border-t pb-6 pt-4" : "border-t py-4"}
                      style={{ borderColor: 'rgba(10, 25, 41, 0.08)' }}
                    >
                      <p style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.62rem', letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.55)', marginBottom: '0.45rem' }}>
                        {detail.key}
                      </p>
                      <p
                        className="font-heading italic"
                        style={{ fontSize: '1.1rem', lineHeight: 1.4, color: '#0A1128', textTransform: 'none', WebkitTextStroke: '0.3px currentColor', textShadow: 'none', letterSpacing: '0' }}
                      >
                        {detail.value}
                      </p>
                      {detail.secondary && (
                        <p style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: '0.85rem', color: 'rgba(10, 25, 41, 0.6)', marginTop: '0.25rem' }}>
                          {detail.secondary}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* RSVP button */}
                {!event.is_past && (
                  <button
                    onClick={() => setShowRSVP(true)}
                    className="rsvp-sweep relative overflow-hidden w-full flex items-center justify-center gap-2 mt-7"
                    style={{
                      backgroundColor: '#CBA365',
                      color: '#0A1128',
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      letterSpacing: '0.32em',
                      textTransform: 'uppercase',
                      padding: '1.2rem 2rem',
                      transition: 'color 0.4s ease',
                    }}
                    data-testid="rsvp-button"
                  >
                    <span
                      aria-hidden="true"
                      className="rsvp-sweep__fill absolute inset-0"
                      style={{ backgroundColor: '#0A1128' }}
                    />
                    <span className="relative z-10 flex items-center gap-2">
                      RSVP <ArrowRight size={15} />
                    </span>
                  </button>
                )}

                {/* Share + Calendar links */}
                <div
                  className="flex items-center justify-center gap-6 mt-5 pt-5"
                  style={{ borderTop: '1px solid rgba(10, 25, 41, 0.08)' }}
                >
                  <button
                    onClick={handleShare}
                    className="group inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#CBA365]"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.62rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.55)' }}
                  >
                    <Share2 size={12} /> Share
                  </button>
                  <button
                    onClick={handleAddToCalendar}
                    data-testid="add-to-calendar-btn"
                    className="group inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#CBA365]"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.62rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(10, 25, 41, 0.55)' }}
                  >
                    <CalendarPlus size={12} /> Add to Calendar
                  </button>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {showRSVP && <RSVPModal event={event} onClose={() => setShowRSVP(false)} />}
    </div>
  );
}
